import React, { useRef, useState, useEffect } from 'react';
import { Upload, FileText, X } from 'lucide-react';
import { toast } from 'sonner';
import { getImageUrl } from '../../utils/assetUrlHelper';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Allowed file types for different field purposes
export const FILE_ACCEPT_TYPES = {
    image: 'image/jpeg,image/png,image/webp,image/gif',
    pdf: 'application/pdf',
    document: 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    video: 'video/mp4,video/webm,video/ogg',
    all: 'image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,video/*'
};

// File Upload Component with Multiple Support and Preview
const FileUploadInput = ({
    field,
    value, // Current file object(s) in state
    onChange, // Callback when file is selected/removed
    existingUrl, // Existing URL(s) from server
    formData, // Full form data (optional, for updating related fields)
    setFormData, // Setter for form data (optional)
    onDeleteServerFile // Callback to handle server-side deletion
}) => {
    const inputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [previews, setPreviews] = useState([]);
    const [files, setFiles] = useState([]);

    // Determine configuration based on field definition or props
    const acceptType = (field && field.fileType && FILE_ACCEPT_TYPES[field.fileType]) || FILE_ACCEPT_TYPES.all;
    const isMultiple = field && field.multiple === true;
    const fieldName = field ? field.name : 'unknown_file';

    // Initialize from existing values (URLs or Files)
    useEffect(() => {
        if (existingUrl) {
            const urls = Array.isArray(existingUrl) ? existingUrl : [existingUrl];
            setPreviews(urls.map(url => {
                if (typeof url !== 'string') return null;
                return getImageUrl(url);
            }).filter(Boolean));
        } else {
            setPreviews([]);
        }
    }, [existingUrl]);

    // Sync internal files state with prop value if provided
    useEffect(() => {
        if (value) {
            if (Array.isArray(value)) {
                setFiles(value);
            } else if (value instanceof File) {
                setFiles([value]);
            } else {
                setFiles([]);
            }
        } else {
            setFiles([]);
        }
    }, [value]);

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files || []);
        processFiles(selectedFiles);
    };

    const processFiles = (selectedFiles) => {
        if (selectedFiles.length === 0) return;

        // Validate file size (50MB max)
        const validFiles = selectedFiles.filter(file => {
            if (file.size > 50 * 1024 * 1024) {
                toast.error(`File ${file.name} is too large. Maximum size is 50MB.`);
                return false;
            }
            return true;
        });

        if (validFiles.length === 0) return;

        let newFiles;
        if (isMultiple) {
            newFiles = [...files, ...validFiles];
        } else {
            newFiles = [validFiles[0]];
        }

        setFiles(newFiles);
        onChange(isMultiple ? newFiles : newFiles[0]);

        // Generate previews for images
        if (!isMultiple) {
            // Clear previous previews if single selection
            setPreviews([]);
        }

        validFiles.forEach(file => {
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    if (!isMultiple) {
                        setPreviews([reader.result]);
                    } else {
                        setPreviews(prev => [...prev, reader.result]);
                    }
                };
                reader.readAsDataURL(file);
            } else {
                // For non-image files
                const previewText = 'FILE:' + file.name;
                if (!isMultiple) {
                    setPreviews([previewText]);
                } else {
                    setPreviews(prev => [...prev, previewText]);
                }
            }
        });
    };

    const removeFile = async (index, e) => {
        e.stopPropagation();

        const previewUrl = previews[index];
        let isServerFile = false;
        let serverUrlToDelete = null;

        if (typeof previewUrl === 'string' && !previewUrl.startsWith('data:') && !previewUrl.startsWith('FILE:')) {
            isServerFile = true;
            // Convert full URL back to relative if needed, or extract ID
            serverUrlToDelete = previewUrl.replace(API_URL, '');
        }

        if (isServerFile && onDeleteServerFile) {
            const success = await onDeleteServerFile(serverUrlToDelete);
            if (!success) return; // Abort if deletion failed or cancelled
        }

        // Proceed with UI removal
        const newPreviews = [...previews];
        newPreviews.splice(index, 1);
        setPreviews(newPreviews);

        // Remove from files array if it was a new file
        if (!isServerFile) {
            if (!isMultiple) {
                setFiles([]);
                onChange(null);
            } else {
                // For multiple, simpler to just allow re-upload or clear logic elsewhere.
                // We can't easily map index back to files array without tracking source.
                setFiles([]); // Resetting files for now in multiple scenario to avoid inconsistent state, user can re-upload
                onChange([]);
                toast.info("Validation cleared. Please re-select added files if needed.");
            }
        }

        // Update formData to clear the URL field
        if (setFormData && formData) {
            if (isMultiple) {
                const currentUrls = Array.isArray(existingUrl) ? [...existingUrl] : [];
                if (index < currentUrls.length) {
                    const updatedUrls = currentUrls.filter((_, i) => i !== index);
                    const newData = {
                        ...formData,
                        [fieldName]: updatedUrls
                    };
                    const urlFieldName = fieldName.replace('_file', '_url');
                    if (urlFieldName !== fieldName) {
                        newData[urlFieldName] = JSON.stringify(updatedUrls);
                    }
                    setFormData(newData);
                }
            } else {
                // Clear URL fields
                const newData = {
                    ...formData,
                    [fieldName]: ''
                };
                const urlFieldName = fieldName.replace('_file', '_url');
                if (urlFieldName !== fieldName) {
                    newData[urlFieldName] = '';
                }
                setFormData(newData);
                onChange(null); // Clear the file object
            }
        } else if (!isMultiple && !setFormData) {
            // Fallback for when setFormData is not provided (standalone usage)
            onChange(null);
        }
    };

    const triggerFileSelect = () => {
        inputRef.current?.click();
    };

    return (
        <div className="space-y-3">
            <input
                ref={inputRef}
                type="file"
                accept={acceptType}
                multiple={isMultiple}
                onChange={handleFileChange}
                className="hidden"
            />

            <div
                onClick={triggerFileSelect}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => { e.preventDefault(); setIsDragging(false); processFiles(Array.from(e.dataTransfer.files)); }}
                className={`relative flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-lg cursor-pointer transition-all ${isDragging ? 'border-[#0E6D2B] bg-green-50' : 'border-gray-300 hover:border-[#0E6D2B] hover:bg-gray-50'
                    }`}
            >
                <Upload className="w-10 h-10 text-gray-400 mb-2" />
                <p className="text-sm font-medium text-gray-700">Click to upload or drag and drop</p>
                <p className="text-xs text-gray-500 mt-1">
                    {isMultiple ? 'You can select multiple files' : 'Select a file'} (Max 50MB each)
                </p>
            </div>

            {previews.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-4">
                    {previews.map((preview, index) => (
                        <div key={index} className="relative aspect-square border rounded-lg overflow-hidden bg-gray-50 group">
                            {preview.startsWith('data:image') || preview.match(/\.(jpg|jpeg|png|webp|gif)/i) || (typeof preview === 'string' && preview.includes('http')) ? (
                                <img src={preview} alt="Preview" className="w-full h-full object-cover" onError={(e) => {e.target.onerror=null; e.target.src="https://placehold.co/100x100?text=Error";}} />
                            ) : (
                                <div className="w-full h-full flex flex-col items-center justify-center p-2 text-center">
                                    <FileText className="w-8 h-8 text-blue-500 mb-1" />
                                    <span className="text-xs break-all">{preview.replace && preview.replace('FILE:', '')}</span>
                                </div>
                            )}
                            <button
                                type="button"
                                onClick={(e) => removeFile(index, e)}
                                className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-70 hover:opacity-100 transition-opacity z-10"
                                title="Remove (Deletes from server if saved)"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default FileUploadInput;
